import React from "react";

import { useNavigate } from "react-router";

import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as Spaces from "@/models/spaces";

import { applyAccessLevelConstraints, initialAccessLevels } from "@/features/spaces";
import { usePaths } from "@/routes/paths";
import { PageModule } from "@/routes/types";
import { AccessLevelSummary, Forms, SecondaryButton, useFormContext } from "turboui";
import { useTranslation } from "react-i18next";
import { translationText } from "@/i18n";

export default { name: "SpaceAddPage", loader: Pages.emptyLoader, Page } as PageModule;

function Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = Spaces.useCreateSpace();
  const paths = usePaths();

  const form = Forms.useForm({
    fields: {
      name: "",
      mission: "",
      access: initialAccessLevels(),
      showAdvancedAccess: false,
    },
    onChange: ({ newValues }) => {
      newValues.access = applyAccessLevelConstraints(newValues.access);
    },
    submit: async () => {
      const res = await create.mutateAsync({
        name: form.values.name,
        mission: form.values.mission,
        publicPermissions: form.values.access.anonymous,
        companyPermissions: form.values.access.companyMembers,
      });

      if (!res.space?.id) {
        throw new Error("Created space is unavailable");
      }

      navigate(paths.spacePath(res.space.id));
    },
    onError: (error) => {
      const data = error.response?.data as { error?: string; message?: string } | undefined;
      const message = data?.message || translationText(t("There was an unexpected error. Please try again later."));

      form.actions.addErrors({ _submit: message });
    },
  });

  return (
    <Pages.Page title={translationText(t("Create a new space"))}>
      <Paper.Root size="small">
        <Paper.NavigateBack to={paths.homePath()} title={t("Back to Home")} />
        <Title />

        <Forms.Form form={form}>
          <Paper.Body minHeight="none">
            <Forms.FieldGroup>
              <NameInput field="name" />
              <PurposeInput field="mission" />
            </Forms.FieldGroup>

            <Forms.FormError message={form.errors._submit} when={!!form.errors._submit} className="mt-4" />

            <PrivacyLevel />
          </Paper.Body>

          <Forms.Submit saveText={translationText(t("Create Space"))} layout="centered" buttonSize="base" />
        </Forms.Form>
      </Paper.Root>
    </Pages.Page>
  );
}

function Title() {
  const { t } = useTranslation();

  return (
    <div className="text-center mb-6">
      <h1 className="text-3xl font-bold">{t("Create a new space")}</h1>
      <span className="text-content-dimmed">{t("Spaces help organize projects, goals, and team members in one place.")}</span>
    </div>
  );
}

function NameInput({ field }: { field: string }) {
  const { t } = useTranslation();
  const form = useFormContext();

  return (
    <Forms.TextInput
      label={translationText(t("Space Name"))}
      field={field}
      placeholder={translationText(t("e.g. Marketing"))}
      required
      autoFocus
      onEnter={(event) => {
        event.preventDefault();
        void form.actions.submit();
      }}
    />
  );
}

function PurposeInput({ field }: { field: string }) {
  const { t } = useTranslation();

  return (
    <Forms.TextInput
      label={translationText(t("Purpose"))}
      field={field}
      placeholder={translationText(t("e.g. Create product awareness and bring new leads"))}
      required
    />
  );
}

function PrivacyLevel() {
  const [isAdvanced] = Forms.useFieldValue<boolean>("showAdvancedAccess");

  return (
    <Paper.DimmedSection>
      <div className="flex items-center justify-between">
        <PrivacyLevelTitle field={"access"} />
        <PrivacyEdit />
      </div>

      {isAdvanced && <Forms.AccessSelectors showSpaceAccess={false} />}
    </Paper.DimmedSection>
  );
}

function PrivacyLevelTitle({ field }: { field: string }) {
  const [anonymous] = Forms.useFieldValue<number>(`${field}.anonymous`);
  const [company] = Forms.useFieldValue<number>(`${field}.companyMembers`);

  if (anonymous === undefined || company === undefined) {
    return null;
  }

  return <AccessLevelSummary resourceType="space" tense="future" anonymous={anonymous} company={company} hideIcon />;
}

function PrivacyEdit() {
  const { t } = useTranslation();
  const [isAdvanced, setIsAdvanced] = Forms.useFieldValue<boolean>("showAdvancedAccess");
  if (isAdvanced) return null;

  return (
    <SecondaryButton size="xs" onClick={() => setIsAdvanced(true)} testId="edit-access-levels">
      {t("Edit")}
    </SecondaryButton>
  );
}
